import type { Goal } from '../content/types';
import type { CardRecord, Grade } from '../lib/scheduler';

export type ThemePreference = 'system' | 'light' | 'dark';

export interface Settings {
  /** New phrases a day, 3 to 10. */
  newPerDay: number;
  /** Days a week the learner aims to practise; a target, never a streak. */
  weeklyTarget: number;
  /** Show the sounds-like spelling under the Greek without tapping. */
  showRespelling: boolean;
  /** Stop recording by itself after the learner stops speaking. */
  autoStop: boolean;
  theme: ThemePreference;
}

export type FlagReason = 'greek' | 'meaning' | 'audio' | 'note' | 'other';

export interface Flag {
  reason: FlagReason;
  note?: string;
  at: string;
}

export interface Profile {
  createdAt: string;
  onboarded: boolean;
  /** Why they learn: a holiday (the short Tourist track) or a life in Greece (Expat, to B2). */
  goal: Goal | null;
  /** The if-then habit cue: "After my morning coffee". */
  habit: string | null;
  lessonsDone: string[];
  /** Learning days with a completed session. */
  practiceDays: string[];
  /** Graded cards per learning day, for the review-load brake. */
  reviewsByDay: Record<string, number>;
  /** Extra new phrases the learner chose to take early, for one day. */
  extraNew: { day: string; count: number } | null;
  flags: Record<string, Flag>;
  /** The learning day on which "Can't speak now" is on. */
  quietDay: string | null;
  /** The microphone explanation has been shown. */
  micExplained: boolean;
  /** The reading guide has been opened. */
  readingGuideSeen: boolean;
  /** Each finished lesson's "Try it for real": the day it was set, and the learner's answer next time. */
  missions?: Record<string, { day: string; tried?: boolean }>;
  /** Story episodes: when first opened and finished, how often watched, the phrases kept. */
  stories?: Record<string, StoryProgress>;
}

export interface StoryProgress {
  started: string;
  finished?: string;
  /** Times watched to the end. */
  watches: number;
  kept?: string[];
}

export type Step =
  | { kind: 'review'; cardId: string }
  | { kind: 'lesson-intro'; lessonId: string }
  | { kind: 'meet'; itemId: string }
  | { kind: 'first'; cardId: string }
  | { kind: 'contrast'; itemIds: string[] }
  | { kind: 'scene'; lessonId: string }
  | { kind: 'lesson-done'; lessonId: string }
  | { kind: 'repeat'; cardId: string };

export interface SessionState {
  id: string;
  day: string;
  steps: Step[];
  index: number;
  /** Phrases met for the first time in this session. */
  met: string[];
  /** Cards graded in this session, with their latest grade. */
  graded: Record<string, Grade>;
  /** Step indexes already answered, so a resumed session never grades a step twice. */
  answered?: number[];
  /** A step skipped because its audio would not play. */
  skipped: number;
  quiet: boolean;
  /** Planned as "just the reviews". */
  reviewsOnly?: boolean;
  startedAt: string;
  completedAt: string | null;
}

export interface Persisted {
  version: 1;
  cards: Record<string, CardRecord>;
  settings: Settings;
  profile: Profile;
  session: SessionState | null;
}

export interface Attempt {
  cardId: string;
  at: string;
  grade: Grade;
  /** The meaning picked on a hear-it card. */
  pick?: string;
  repeat: boolean;
  /** The check right after meeting the phrase. */
  learning: boolean;
  hint: boolean;
  recorded: boolean;
  /** The rough match with the model, 0–100 (null: no speech heard), when recorded. */
  match?: number | null;
  contentVersion: string;
  sessionId: string;
}

export const DEFAULT_SETTINGS: Settings = {
  newPerDay: 5,
  weeklyTarget: 4,
  showRespelling: false,
  autoStop: true,
  theme: 'system',
};

export function freshState(now: Date): Persisted {
  return {
    version: 1,
    cards: {},
    settings: { ...DEFAULT_SETTINGS },
    profile: {
      createdAt: now.toISOString(),
      onboarded: false,
      goal: null,
      habit: null,
      lessonsDone: [],
      practiceDays: [],
      reviewsByDay: {},
      extraNew: null,
      flags: {},
      quietDay: null,
      micExplained: false,
      readingGuideSeen: false,
    },
    session: null,
  };
}
